import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePursuit } from '../contexts/PursuitContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { LATEST_POLICY_VERSION, LEGAL_PAGES_HIDDEN } from '../lib/version';
import PolicyAcceptanceModal from '../components/modals/PolicyAcceptanceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyAuthenticatorPage() {
  const navigate = useNavigate();
  const { loginWithJWT } = useAuth();
  const { clearPursuitState } = usePursuit();

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const inputRefs = useRef([]);

  const preAuthToken = sessionStorage.getItem('pre_auth_token');

  useEffect(() => {
    if (!preAuthToken) navigate('/login');
  }, [preAuthToken, navigate]);

  const code = digits.join('');

  const handleDigitChange = (idx, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = digit;
    setDigits(next);
    setError(null);
    if (digit && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const finishLogin = (data) => {
    sessionStorage.removeItem('pre_auth_token');
    localStorage.removeItem('auth_token');
    localStorage.setItem('auth_user', JSON.stringify(data.user));
    if (loginWithJWT) loginWithJWT(data.access_token, data.user);

    const role = data.user.role;
    const slug = data.user.organization_slug;
    let target = role === 'SUPER_ADMIN' ? '/admin/dashboard' : `/${slug}/dashboard`;

    const hasPolicy = LEGAL_PAGES_HIDDEN || (
      !!data.user.policy_accepted_at && data.user.policy_version === LATEST_POLICY_VERSION
    );
    if (!hasPolicy) {
      setPendingNavigation(target);
      setShowPolicyModal(true);
    } else {
      navigate(target);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) { setError('Enter all 6 digits'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-totp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${preAuthToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid code');
      clearPursuitState();
      finishLogin(data);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit on 6 digits
  useEffect(() => {
    if (code.length === 6 && !isLoading) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/login" className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4 text-center">
            <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-7 h-7 text-teal-700" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Two-factor authentication
            </CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    autoFocus={i === 0}
                    data-testid={`totp-digit-${i}`}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-lg focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600/20 transition-colors"
                  />
                ))}
              </div>

              <Button
                type="submit"
                className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                disabled={isLoading || code.length !== 6}
                data-testid="verify-totp-btn"
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  'Verify & sign in'
                )}
              </Button>

              <p className="text-center text-xs text-slate-400">
                Code changes every 30 seconds. Make sure your device time is correct.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      <PolicyAcceptanceModal
        isOpen={showPolicyModal}
        onAccepted={() => {
          setShowPolicyModal(false);
          if (pendingNavigation) navigate(pendingNavigation);
        }}
      />
    </div>
  );
}
